/**
 * LookScoreApiService — Wrapper du backend POST /score-look.
 *
 * Endpoint : `${looksApiUrl}/score-look`
 * Auth : JWT Cognito obligatoire.
 *
 * Payload :
 *   { selectedItems[], context: { tempC, occasion, style }, gender }
 *
 * Réponse :
 *   {
 *     score: 0-100,
 *     grade: { icon, labelKey },
 *     breakdown: {
 *       color: { score, weight, banksApplied, explanation, explanationEn },
 *       material: { score, weight, banksApplied, explanation, explanationEn },
 *       style: { score, weight, banksApplied, explanation, explanationEn, tagDetail },
 *       formality: { score, weight, banksApplied, explanation, explanationEn },
 *       weather: { score, weight, banksApplied, explanation, explanationEn },
 *     },
 *     aiVision: { score, coverage, explanation, explanationEn },
 *     issues: OutfitIssue[],
 *     context: { tempC, occasion, style },
 *     banksApplied: { color, material, style },
 *   }
 *
 * OutfitIssue :
 *   {
 *     type: 'color' | 'formality' | 'style' | 'weather' | 'material',
 *     severity: 'high' | 'medium' | 'low',
 *     fr: string, en: string,
 *     itemIds: string[],
 *     fix?: { categoryToReplace, itemIdToReplace, itemNameToReplace, fr, en },
 *   }
 */

import { AWS_CONFIG } from '../config/aws_config';
import * as SecureStore from '../utils/secureStore';

const API_BASE = AWS_CONFIG.looksApiUrl;

async function getAuthToken() {
  try { return await SecureStore.getItemAsync('outfit_access'); } catch { return null; }
}

/**
 * Appelle /score-look pour obtenir le score backend de l'outfit final.
 *
 * @param {object} args
 * @param {Array} args.selectedItems  - items composant le look (avec ai_metadata)
 * @param {object} [args.context]     - { tempC, occasion, style }
 * @param {string} [args.gender]      - 'F' | 'M' | 'unisex'
 * @returns {Promise<object>} ScoreLookResponse
 */
export async function fetchScoreLook(args) {
  if (!API_BASE) throw new Error('looksApiUrl manquant');
  const token = await getAuthToken();
  if (!token) throw new Error('fetchScoreLook: JWT requis');

  const url = `${API_BASE}/score-look`;
  const body = JSON.stringify({
    selectedItems: args.selectedItems || [],
    context: args.context || {},
    gender: args.gender || 'F',
  });

  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body,
  });
  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`fetchScoreLook failed (${res.status}): ${errBody.slice(0, 200)}`);
  }
  return res.json();
}
